import { Link } from "react-router-dom";
import { Lock, BadgeCheck, Eye, FileCheck, Star, ShieldCheck, Landmark, ScrollText } from "lucide-react";
import { GOOGLE_REVIEW_URL } from "@/lib/seo-config";

export default function Footer() {
  return (
    <footer className="bg-[#0A0A0B] text-zinc-400 py-12" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-5 gap-8 text-sm">
        <div className="md:col-span-1">
          <div className="display text-2xl text-white">Batisur</div>
          <div className="mono text-[10px] uppercase tracking-widest mt-2">// Paiement sécurisé · BTP</div>
          <p className="mt-4 text-xs leading-relaxed">
            Plateforme française d'escrow bancaire européen pour tous les travaux du bâtiment.
            300+ villes couvertes, 25 métiers référencés.
          </p>
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-white mb-3">Plateforme</div>
          <Link to="/artisan" className="block hover:text-white py-1" data-testid="footer-link-metiers">Artisans par métier</Link>
          <Link to="/artisans/villes" className="block hover:text-white py-1" data-testid="footer-link-cities">Annuaire par ville</Link>
          <Link to="/pour-qui" className="block hover:text-white py-1" data-testid="footer-link-forwho">Pour qui est Batisur ?</Link>
          <Link to="/pro/rejoindre" className="block hover:text-white py-1" data-testid="footer-link-join-artisan">Devenir artisan Batisur</Link>
          <Link to="/estimer" className="block hover:text-white py-1" data-testid="footer-link-estimer">Estimateur gratuit</Link>
          <Link to="/pro/premium" className="block hover:text-white py-1" data-testid="footer-link-premium">Packs Premium / Or</Link>
          <Link to="/blog" className="block hover:text-white py-1" data-testid="footer-link-blog">Blog</Link>
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-white mb-3">Sécurité & Conformité</div>
          <div className="py-1 flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-[#FF4D00]" /> Séquestre bancaire Stripe</div>
          <div className="py-1 flex items-center gap-2"><Landmark className="w-3.5 h-3.5 text-[#FF4D00]" /> Compte bancaire européen agréé</div>
          <div className="py-1 flex items-center gap-2"><BadgeCheck className="w-3.5 h-3.5 text-[#FF4D00]" /> SIRET vérifié INSEE Sirene</div>
          <div className="py-1 flex items-center gap-2"><Eye className="w-3.5 h-3.5 text-[#FF4D00]" /> Preuves photo SHA-256 horodatées</div>
          <div className="py-1 flex items-center gap-2"><FileCheck className="w-3.5 h-3.5 text-[#FF4D00]" /> Factur-X 2026 conforme</div>
          <div className="py-1 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#FF4D00]" /> Décennale artisan vérifiée</div>
          <div className="py-1 flex items-center gap-2"><ScrollText className="w-3.5 h-3.5 text-[#FF4D00]" /> RGPD · données FR/UE</div>
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-white mb-3">Légal</div>
          <Link to="/mentions-legales" className="block hover:text-white py-1" data-testid="footer-link-mentions">Mentions légales</Link>
          <Link to="/cgu" className="block hover:text-white py-1" data-testid="footer-link-cgu">CGU</Link>
          <Link to="/cgv" className="block hover:text-white py-1" data-testid="footer-link-cgv">CGV</Link>
          <div className="mt-4 text-[10px] leading-relaxed text-zinc-500">
            <div><strong className="text-zinc-300">SAS BATISUR CONNECT</strong></div>
            <div>SIREN 107 638 520</div>
            <div>29 rue Tronchet, 75008 Paris</div>
          </div>
        </div>
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-white mb-3">Compte</div>
          <Link to="/login" className="block hover:text-white py-1" data-testid="footer-link-login">Connexion</Link>
          <Link to="/register" className="block hover:text-white py-1" data-testid="footer-link-register">Inscription</Link>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-white py-1 mt-3 flex items-center gap-2 text-[#FF4D00]"
            data-testid="footer-link-google-review"
          >
            <Star className="w-3.5 h-3.5" fill="#FF4D00" /> Laisser un avis Google
          </a>
        </div>
      </div>

      {/* E-E-A-T Trust Row : partenariats + conformité */}
      <div className="max-w-7xl mx-auto px-6 mt-10 pt-6 border-t border-white/10">
        <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">// Partenaires & conformité</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-[11px] leading-snug">
          <div>
            <div className="text-white font-semibold flex items-center gap-1.5">
              <Lock className="w-3.5 h-3.5 text-[#635BFF]" /> Partenaire Stripe
            </div>
            <p className="mt-1 text-zinc-500">
              Séquestre géré via <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="text-[#FF4D00] hover:underline">Stripe</a>, opérateur bancaire européen régulé (agrément CSSF Luxembourg &amp; ACPR France).
            </p>
          </div>
          <div>
            <div className="text-white font-semibold flex items-center gap-1.5">
              <FileCheck className="w-3.5 h-3.5 text-[#FF4D00]" /> Factur-X 2026
            </div>
            <p className="mt-1 text-zinc-500">
              Factures 100% conformes à la réforme française de la facturation électronique B2B obligatoire (loi n°2023-1322 · sept. 2026).
            </p>
          </div>
          <div>
            <div className="text-white font-semibold flex items-center gap-1.5">
              <BadgeCheck className="w-3.5 h-3.5 text-[#FF4D00]" /> INSEE Sirene
            </div>
            <p className="mt-1 text-zinc-500">
              Chaque artisan est vérifié en temps réel via l'API officielle INSEE Sirene. Zéro faux professionnel toléré.
            </p>
          </div>
          <div>
            <div className="text-white font-semibold flex items-center gap-1.5">
              <ScrollText className="w-3.5 h-3.5 text-[#FF4D00]" /> RGPD · Hébergement UE
            </div>
            <p className="mt-1 text-zinc-500">
              Données hébergées en France/UE (OVHcloud). Conformité pleine RGPD. Politique de confidentialité claire.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-8 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div>
          © {new Date().getFullYear()} <span className="text-white">SAS BATISUR CONNECT</span> · SIREN 107 638 520 · Tous droits réservés.
        </div>
        <div className="mono uppercase tracking-widest text-[10px]">Made with structural rigor · Built in France</div>
      </div>
    </footer>
  );
}
